import { notEmpty } from "@product/scmp-sdk";
import { useRouter } from "next/router";
import type { MouseEvent } from "react";
import { forwardRef, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import {
  checkCanShowInPlusPlatform,
  checkIsPlusArticle,
} from "scmp-app/components/article/article-render/plus/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { pathname as plusSearchPathname } from "scmp-app/pages/plus/search";
import type { searchContentItemContent$key } from "scmp-app/queries/__generated__/searchContentItemContent.graphql";

import {
  Authors,
  Container,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  PrintHeadline,
  StyledEntityLink,
  StyledImagePlusFlag,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  isShowPlusFlag?: boolean;
  reference: searchContentItemContent$key;
};

export const ContentItemSearch = forwardRef<HTMLDivElement, Props>(
  ({ className, isShowPlusFlag = false, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment searchContentItemContent on Content {
          entityId
          authors {
            entityId
          }
          topics {
            entityId
          }
          sections {
            value {
              entityId
            }
          }
          ...entityLink
          ...helpersUseContentSponsorContent
          ...hooksContentItemProvidersContent
            @arguments(
              withLiveTag: true
              withHeadline: true
              withCoverImage: true
              withCoverImageSize80x80: true
              withSummary: true
              withActionBar: true
              withPrintHeadline: true
              withSponsorTag: true
              withAuthorImage: true
              withAuthorLink: true
            )
          ...hooksPlusArticleLinkHandlerArticle
          ...helpersCheckCanShowInPlusPlatformArticle
          ...helpersCheckIsPlusArticle
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);
    const { route } = useRouter();

    const isOnPlusSearch = useMemo(() => route === plusSearchPathname, [route]);

    const needToShowPlusFlag = useMemo(
      () => isShowPlusFlag && checkIsPlusArticle(content),
      [content, isShowPlusFlag],
    );

    const header = useMemo(
      () => (
        <>
          {needToShowPlusFlag && <StyledImagePlusFlag />}
          <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
          {providers.headline({
            hideTooltip: true,
            useSectionTags: true,
          })}
        </>
      ),
      [needToShowPlusFlag, providers],
    );

    const cover = providers.coverImage({
      responsiveVariants: { desktopUp: "size80x80", mobileUp: "size80x80", tabletUp: "size80x80" },
    });

    const contentSponsor = getContentSponsor(content);
    const isSponsorArticle = notEmpty(contentSponsor?.name);
    const summary = isSponsorArticle ? null : providers.summary();

    const { currentValue } = useSearchContext();

    const authors = useMemo(
      () => (
        <>
          {providers.authorImage({
            ignoreAuthorType: true,
          })}
          {providers.author({
            ignoreAuthorType: true,
            isMultiple: true,
            onClick: (_entityUuid: string, entityId?: string) => {
              // GA4 21.2.1
              sendGA4Tracking({
                action: "click",
                category: "search",
                customized_parameters: {
                  author_id: entityId,
                  is_plus_article: needToShowPlusFlag ? "yes" : "no",
                  search_text: currentValue,
                },
                subcategory: "result",
              });
            },
          })}
        </>
      ),
      [currentValue, needToShowPlusFlag, providers],
    );

    const listOfSectionIds =
      content.sections?.[0]?.value?.map(section => section?.entityId).join(",") ?? "";

    const listOfTopicIds = content.topics?.map(topic => topic?.entityId).join(",") ?? "";

    const { handleOpen } = usePlusArticlePopup();
    const isShowOnPlus = checkCanShowInPlusPlatform(content);

    const handleItemClick = useCallback(
      (event: MouseEvent) => {
        // GA4 21.2.1
        sendGA4Tracking({
          action: "click",
          category: "search",
          customized_parameters: {
            article_id: content?.entityId,
            author_id: content?.authors?.[0]?.entityId,
            is_plus_article: needToShowPlusFlag ? "yes" : "no",
            search_text: currentValue,
            section_id: listOfSectionIds,
            topic_id: listOfTopicIds,
          },
          subcategory: "result",
        } as SearchEvent);

        if (isOnPlusSearch && isShowOnPlus) {
          event.preventDefault();
          handleOpen(content);
        }
      },
      [
        content,
        currentValue,
        handleOpen,
        isOnPlusSearch,
        isShowOnPlus,
        listOfSectionIds,
        listOfTopicIds,
        needToShowPlusFlag,
      ],
    );

    const footer = isSponsorArticle
      ? providers.sponsorTag?.({
          color: "#001246",
          gap: "4px",
          layoutDirection: "horizontal",
          nameFontSize: "12px",
          typeFontSize: "12px",
        })
      : providers.actionBar({
          displayDateVariant: "publishedDate",
          isBookmarkVisible: false,
          isCommentsVisible: false,
        });

    const renderContent = useCallback(() => {
      const resultContent = (
        <>
          <Header>{header}</Header>
          <Cover>{cover}</Cover>
          <Authors>{authors}</Authors>
          <Summary>{summary}</Summary>
          <Footer>{footer}</Footer>
          <PrintHeadline>{providers.printHeadline()}</PrintHeadline>
        </>
      );

      return (
        <StyledEntityLink
          onClick={handleItemClick}
          reference={content}
          target={isOnPlusSearch ? "_blank" : "_self"}
        >
          {resultContent}
        </StyledEntityLink>
      );
    }, [
      authors,
      content,
      cover,
      footer,
      handleItemClick,
      header,
      isOnPlusSearch,
      providers,
      summary,
    ]);

    return (
      <Container className={className} ref={reference}>
        {renderContent()}
      </Container>
    );
  },
);

ContentItemSearch.displayName = "ContentItemSearch";
